// Phase 999.4 Plan 07 — Step A: pick existing org or create new (R6, D-13).
//
// CONTEXT.md D-13 locks the UX: combobox-with-typeahead over GET /api/admin/orgs
// + an explicit "Create new <typed>" affordance when no exact match exists.
// Selecting an existing org dispatches SET_ORG with {id, name, description};
// selecting "Create new" reveals the description field and dispatches with
// id:null. The id===null discriminator is read by buildPayloadFromState.
//
// This step issues exactly ONE GET (/api/admin/orgs) on mount — no POST/PUT,
// nothing reaches the server's transactional surface (R8). Failures fall back
// to the "create new" path so an outage at the orgs endpoint never strands an
// operator who is creating a brand-new customer anyway.

import React, { useState, useEffect } from 'react';
import { Form, FormGroup, Label, Input, FormText, Alert } from 'reactstrap';
import { apiBase } from '../apiBase';

export function StepA_PickOrCreateOrg({ state, dispatch, auth }) {
    const [orgs, setOrgs] = useState([]);
    const [typed, setTyped] = useState(state.org.name || '');
    // mode: 'existing' (user picked from list) | 'create' (user clicked Create new) | null (still typing)
    const [mode, setMode] = useState(state.org.id ? 'existing' : (state.org.name ? 'create' : null));
    const [loadError, setLoadError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                let token = '';
                if (auth && typeof auth.getAccessToken === 'function') {
                    try { token = auth.getAccessToken(); } catch { /* unauth — server gate handles */ }
                }
                const res = await fetch(apiBase() + '/api/admin/orgs', {
                    headers: token ? { Authorization: 'Bearer ' + token } : {},
                });
                if (!res.ok) throw new Error('HTTP ' + res.status);
                const body = await res.json();
                if (!cancelled) setOrgs(Array.isArray(body) ? body : []);
            } catch (e) {
                if (!cancelled) setLoadError(e && e.message ? e.message : String(e));
            }
        })();
        return () => { cancelled = true; };
    }, [auth]);

    // Case-insensitive contains-match for the typeahead dropdown.
    const filtered = typed
        ? orgs.filter((o) => (o.name || '').toLowerCase().includes(typed.toLowerCase()))
        : orgs;
    const exactMatch = orgs.find((o) => (o.name || '').toLowerCase() === (typed || '').toLowerCase());

    const onTypedChange = (e) => {
        setTyped(e.target.value);
        // User is typing — neither picked nor created yet. Clear the org so canProceed
        // disables Next until a deliberate pick or create.
        setMode(null);
        if (state.org.id || state.org.name) {
            dispatch({ type: 'SET_ORG', org: { id: null, name: '', description: '' } });
        }
    };

    const onPickExisting = (o) => {
        setMode('existing');
        setTyped(o.name);
        dispatch({
            type: 'SET_ORG',
            org: { id: o.id, name: o.name, description: o.description || '' },
        });
    };

    const onPickCreateNew = () => {
        setMode('create');
        dispatch({
            type: 'SET_ORG',
            org: { id: null, name: typed, description: state.org.description || '' },
        });
    };

    const onDescChange = (e) => {
        dispatch({ type: 'SET_ORG', org: { ...state.org, description: e.target.value } });
    };

    return (
        <Form>
            <h4>Step A — Organisation</h4>
            {loadError && (
                <Alert color="warning">
                    Failed to load existing orgs: {loadError}. You can still create a new one.
                </Alert>
            )}
            <FormGroup>
                <Label for="org-typeahead">Org name</Label>
                <Input
                    id="org-typeahead"
                    type="text"
                    value={typed}
                    onChange={onTypedChange}
                    placeholder="Type to search or create..."
                    data-testid="step-a-typeahead"
                />
                <FormText>Pick an existing organisation or type a new one.</FormText>
            </FormGroup>
            {typed && filtered.length > 0 && mode !== 'create' && (
                <ul className="list-group mb-2" data-testid="step-a-typeahead-list">
                    {filtered.slice(0, 10).map((o) => (
                        <li
                            key={o.id}
                            className={'list-group-item list-group-item-action ' + (state.org.id === o.id ? 'active' : '')}
                            style={{ cursor: 'pointer' }}
                            onClick={() => onPickExisting(o)}
                            data-testid={`step-a-org-${o.id}`}
                        >
                            <strong>{o.name}</strong>
                            {o.description && <span className="text-muted small ms-2">{o.description}</span>}
                            {state.org.id === o.id && <span className="badge bg-primary ms-2">selected</span>}
                        </li>
                    ))}
                </ul>
            )}
            {typed && !exactMatch && mode !== 'existing' && (
                <button
                    type="button"
                    className="btn btn-outline-primary btn-sm mb-2"
                    onClick={onPickCreateNew}
                    data-testid="step-a-create-new-btn"
                >
                    Create new "{typed}"
                </button>
            )}
            {mode === 'create' && (
                <FormGroup>
                    <Label for="org-desc">New organisation description</Label>
                    <Input
                        id="org-desc"
                        type="textarea"
                        rows={2}
                        value={state.org.description}
                        onChange={onDescChange}
                        data-testid="step-a-org-description"
                    />
                </FormGroup>
            )}
        </Form>
    );
}

export default StepA_PickOrCreateOrg;
